/**
 * @file Recharts wrapper for dashboard summary line charts (no visible dots, gradient area, smart date tooltip).
 */
import React, { useId } from 'react';
import { AreaChart, Line, ResponsiveContainer, XAxis, YAxis, Area, Tooltip } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { Payload } from 'recharts/types/component/DefaultTooltipContent';

export type DashboardLinePoint = {
  timestamp: string;
  value: number | null;
  label?: string;
  startDate?: string;
  endDate?: string;
};

export type DashboardLineChartProps = {
  data: DashboardLinePoint[];
  color?: string;
  yAxisLabel?: string;
  valueLabel?: string;
  /** Height in px or percentage; defaults to '100%' to fill parent. */
  height?: number | `${number}%`;
  gradientStops?: { offset: string; opacity: number }[];
  'data-testid'?: string;
};

const DEFAULT_COLOR = '#2f80ed';

/** Normalizes Recharts tooltip `value` (scalar or array) to a display string. */
function formatTooltipValue(value: ValueType | undefined): string | number {
  if (value == null) return '—';
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return String(value);
}

function formatTooltipDate(raw: string) {
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }
  return raw;
}

/** “March 2026” style label for monthly buckets. */
function formatMonthYear(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Week range like “Mar 30 to Apr 5, 2026”, omitting start year when same as end year. */
function formatWeekRange(startIso: string, endIso: string) {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${formatTooltipDate(startIso)} to ${formatTooltipDate(endIso)}`;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
  const endText = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `${startText} to ${endText}`;
}

/**
 * Tooltip title line from a chart point: uses `startDate`/`endDate` span to pick month, week range, or single day;
 * otherwise falls back to `timestamp` / `label`.
 */
function formatPointLabel(point?: DashboardLinePoint): string {
  if (!point) return 'Date: —';
  if (point.startDate && point.endDate) {
    const start = new Date(point.startDate);
    const end = new Date(point.endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const spanDays = Math.max(1, Math.floor((end.getTime() - start.getTime()) / 86400000) + 1);
      if (spanDays >= 27) {
        return `Date: ${formatMonthYear(point.startDate)}`;
      }
      if (spanDays >= 6) {
        return `Date: ${formatWeekRange(point.startDate, point.endDate)}`;
      }
      return `Date: ${formatTooltipDate(point.startDate)}`;
    }
  }
  return `Date: ${formatTooltipDate(point.timestamp || point.label || '')}`;
}

/**
 * Synthetic flat line at zero when `data` is empty so the chart still renders a visible neutral segment.
 */
function flatZeroLineData(): DashboardLinePoint[] {
  const end = Date.now();
  const start = end - 86400000; // 1 day span on X
  return [
    { timestamp: new Date(start).toISOString(), value: 0 },
    { timestamp: new Date(end).toISOString(), value: 0 },
  ];
}

/**
 * Responsive area + line chart: hidden X axis, tooltip with deduped series row, vertical cursor line.
 * @see {@link DashboardLinePoint} for optional `startDate`/`endDate` used in tooltip grouping.
 */
const DashboardLineChart: React.FC<DashboardLineChartProps> = ({
  data,
  color = DEFAULT_COLOR,
  yAxisLabel,
  valueLabel = 'Value',
  height = '100%',
  gradientStops,
  'data-testid': dataTestId,
}) => {
  const hasPoints = data && data.length > 0;
  const normalized = hasPoints ? data : flatZeroLineData();

  const numericMax = normalized.reduce((m, p) => Math.max(m, p.value ?? 0), 0);
  // Avoid Y domain [0,0] so the zero line still has a stable band to render in
  const yMax = numericMax <= 0 ? 1 : numericMax;

  const stops =
    gradientStops ??
    [
      // Stronger gradient so the area clearly reflects the line color
      { offset: '0%', opacity: 0.7 },
      { offset: '55%', opacity: 0.3 },
      { offset: '100%', opacity: 0 },
    ];

  const gradientId = useId();

  return (
    <ResponsiveContainer width="100%" height={height} data-testid={dataTestId}>
      <AreaChart data={normalized} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`dashboardLineFill-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            {stops.map(stop => (
              <stop key={stop.offset} offset={stop.offset} stopColor={color} stopOpacity={stop.opacity} />
            ))}
          </linearGradient>
        </defs>
        <XAxis dataKey="timestamp" hide />
        <YAxis
          hide
          domain={[0, yMax]}
          label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
        />
        <Tooltip
          cursor={{ stroke: color, strokeDasharray: '4 4', strokeWidth: 1, opacity: 0.4 }}
          separator=": "
          itemSorter={() => -1}
          contentStyle={{
            border: '1px solid #d8dde3',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            padding: '8px 10px',
            background: '#ffffff',
          }}
          formatter={(value: ValueType | undefined, _name: NameType | undefined, _item: unknown, index: number) =>
            (index === 0 ? [formatTooltipValue(value), valueLabel] as [string | number, NameType] : null)
          }
          labelFormatter={(
            _label: React.ReactNode,
            payload?: ReadonlyArray<Payload<ValueType, NameType>>,
          ) => formatPointLabel(payload?.[0]?.payload as DashboardLinePoint | undefined)}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke="none"
          fill={`url(#dashboardLineFill-${gradientId})`}
          dot={false}
          activeDot={false}
          isAnimationActive={true}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={3}
          dot={false}
          activeDot={true}
          isAnimationActive={true}
          connectNulls
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default DashboardLineChart;

